// ## Functionality
// This script adds a click event listener to elements with the attribute `[data-name="sidebar-menu-item"]`. When clicked, it scrolls the element with the attribute `[data-name="side-bar-scroller"]` into view.

// ## Usage
// 1. Include this script in your HTML file.
// 2. Ensure elements with `[data-name="sidebar-menu-item"]` exist in your document.
// 3. Ensure an element with `[data-name="side-bar-scroller"]` exists to scroll into view.

// ## Notes
// - Make sure the elements with the specified data attributes exist in the DOM for the script to work correctly.

(function () {
  // Support both original and AB sidebars
  const allSidebars = document.querySelectorAll('header-sidebar');

  // Function to animate new badges in a container
  function animateNewBadges(container) {
    const badges = container.querySelectorAll('.badge.new-arrivals');
    badges.forEach(badge => {
      badge.classList.remove('subtle-wiggle');
      badge.offsetHeight;
      badge.classList.add('subtle-wiggle');
      setTimeout(() => {
        badge.classList.remove('subtle-wiggle');
      }, 1000);
    });
  }

  function initializeSidebar(sidebar) {
    const level1Buttons = sidebar.querySelectorAll('[data-name="sidebar-menu-item"]');
    const level2Buttons = sidebar.querySelectorAll('[data-name="sidebar-level2-item"]');
    const scroller = sidebar.querySelector('[data-name="side-bar-scroller"]');

    function scrollToTopWhenOpenSubmenu() {
      scroller && scroller.scrollIntoView({ block: "end", inline: "start" });
    }

    // Reset all active states in the sidebar
    function resetAllActiveStates(sidebar) {
      const level2Buttons = sidebar.querySelectorAll('[data-name="sidebar-level2-item"]');
      const level3Panel = sidebar.querySelector('header-sidebar-collapsible-panel.header-sidebar__collapsible-panel[data-level="3"]');

      // Reset all level 2 buttons
      level2Buttons.forEach(btn => btn.setAttribute('aria-expanded', 'false'));

      // Reset level 3 panel
      level3Panel?.removeAttribute('aria-activedescendant');
    }

    // Level 1 → open level 2
    level1Buttons.forEach((el) => el.addEventListener("click", function () {
      // Reset active states in all other submenus
      level1Buttons.forEach(btn => {
        if (btn !== el && btn.getAttribute('aria-expanded') === 'true') {
          resetAllActiveStates(sidebar);
        }
      });

      scrollToTopWhenOpenSubmenu();

      // Get the target panel immediately
      const panelId = el.getAttribute('aria-controls');
      const panel = sidebar.querySelector(`#${panelId}`);

      // Animate badges in the clicked menu's panel
      if (panel) {
        animateNewBadges(panel);
      }

      setTimeout(() => updateBorderRadius(sidebar), 100);
    }));

    // Level 2 → open level 3 in a sibling column
    level2Buttons.forEach((btn) => {
      btn.addEventListener("click", function () {
        // Find level-3 panel within the same sidebar
        const level3Panel = sidebar.querySelector('header-sidebar-collapsible-panel.header-sidebar__collapsible-panel[data-level="3"]');
        const targetId = btn.getAttribute('aria-controls');
        if (level3Panel && targetId) {
          level3Panel.setAttribute('aria-activedescendant', targetId);
          level2Buttons.forEach(b => b.setAttribute('aria-expanded', b === btn ? 'true' : 'false'));
          scrollToTopWhenOpenSubmenu();

          // Get the target panel immediately
          const panel = sidebar.querySelector(`#${targetId}`);

          // Animate badges in the clicked menu's panel
          if (panel) {
            animateNewBadges(panel);
          }

          setTimeout(() => updateBorderRadius(sidebar), 100);
        }
      });
    });

    return { level1Buttons, level2Buttons };
  }

  // Initialize all sidebars
  const allButtons = { level1: [], level2: [] };
  allSidebars.forEach(sidebar => {
    const buttons = initializeSidebar(sidebar);
    allButtons.level1.push(...buttons.level1Buttons);
    allButtons.level2.push(...buttons.level2Buttons);
  });

  // Close tertiary (mobile back)
  document.addEventListener('click', function (e) {
    const back = e.target.closest('[data-action="close-tertiary-panel"]');
    if (!back) return;
    const headerSidebar = back.closest('header-sidebar');
    const level3Panel = headerSidebar?.querySelector('header-sidebar-collapsible-panel.header-sidebar__collapsible-panel[data-level="3"]');
    level3Panel?.removeAttribute('aria-activedescendant');
    allButtons.level2.forEach(b => b.setAttribute('aria-expanded', 'false'));
    const scroller = headerSidebar?.querySelector('[data-name="side-bar-scroller"]');
    scroller && scroller.scrollIntoView({ block: "end", inline: "start" });
    setTimeout(() => updateBorderRadius(headerSidebar), 100); // Update after panel closes
  });

  // Close whole sub-panel (back to level 1) should also reset tertiary state
  document.addEventListener('click', function (e) {
    const back = e.target.closest('[data-action="close-panel"]');
    if (!back) return;
    const headerSidebar = back.closest('header-sidebar');
    const level3Panel = headerSidebar?.querySelector('header-sidebar-collapsible-panel.header-sidebar__collapsible-panel[data-level="3"]');
    level3Panel?.removeAttribute('aria-activedescendant');
    setTimeout(() => updateBorderRadius(headerSidebar), 100); // Update after panel closes
  });

  // Dynamic border-radius management
  function updateBorderRadius(headerSidebar) {
    if (!headerSidebar) return;

    const level2Panel = headerSidebar.querySelector('header-sidebar-collapsible-panel[data-level="2"]');
    const level3Panel = headerSidebar.querySelector('header-sidebar-collapsible-panel[data-level="3"]');
    const mainPanel = headerSidebar.querySelector('.header-sidebar__main-panel');

    // Check which is the rightmost visible panel
    const level3Visible = level3Panel?.hasAttribute('aria-activedescendant');
    const level2Visible = level2Panel?.hasAttribute('aria-activedescendant');

    // Only update if there's actually a change to prevent flickering
    const currentState = headerSidebar.dataset.borderState || 'main';
    let newState = 'main';

    if (level3Visible) {
      newState = 'level3';
    } else if (level2Visible) {
      newState = 'level2';
    }

    // Only apply changes if state actually changed
    if (currentState !== newState) {
      headerSidebar.dataset.borderState = newState;

      // Reset all border-radius
      if (mainPanel) mainPanel.style.borderRadius = '';
      if (level2Panel) level2Panel.style.borderRadius = '';
      if (level3Panel) level3Panel.style.borderRadius = '';

      if (newState === 'level3') {
        // Level 3 is visible - apply radius to level 3
        if (level3Panel) level3Panel.style.borderRadius = '0 0 0.75rem 0';
        if (mainPanel) mainPanel.style.borderRadius = '0.75rem 0 0 0.75rem';
      } else if (newState === 'level2') {
        // Level 2 is visible - apply radius to level 2
        if (level2Panel) level2Panel.style.borderRadius = '0 0 0.75rem 0';
        if (mainPanel) mainPanel.style.borderRadius = '0.75rem 0 0 0.75rem';
      } else {
        // Only main panel visible - apply radius to main panel
        if (mainPanel) mainPanel.style.borderRadius = '0.75rem 0.75rem 0.75rem 0.75rem';
      }
    }
  }

  // Update border radius when panels change for all sidebars
  allSidebars.forEach(headerSidebar => {
    const observer = new MutationObserver(() => updateBorderRadius(headerSidebar));
    observer.observe(headerSidebar, {
      attributes: true,
      attributeFilter: ['aria-activedescendant'],
      subtree: true
    });
    // Set initial border radius
    updateBorderRadius(headerSidebar);
  });

  // When whole sidebar hides, ensure we reset any open tertiary panels
  document.addEventListener('dialog:after-hide', function (e) {
    // ONLY react to main sidebar closing, not submenu panels
    if (!e.target.matches('header-sidebar')) return;

    const sidebar = e.target;
    const level3Panel = sidebar.querySelector('header-sidebar-collapsible-panel.header-sidebar__collapsible-panel[data-level="3"]');
    level3Panel?.removeAttribute('aria-activedescendant');
    allButtons.level2.forEach(b => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => updateBorderRadius(sidebar), 50); // Update after state changes

    // Remove lock-header class when sidebar closes
    document.documentElement.classList.remove('lock-header');
  }, true);

  // Trigger wiggle animation ONLY on main sidebar open
  document.addEventListener('dialog:after-show', function (e) {
    // ONLY react to main sidebar opening, not submenu panels
    if (!e.target.matches('header-sidebar')) return;

    const sidebar = e.target;

    // Add lock-header class when sidebar opens
    document.documentElement.classList.add('lock-header');

    // Animate badges in the main panel
    const mainPanel = sidebar.querySelector('.header-sidebar__main-panel');
    if (mainPanel) {
      animateNewBadges(mainPanel);
    }
  }, true);

  // Handle Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      allSidebars.forEach(sidebar => {
        if (sidebar.hasAttribute('open')) {
          resetAllActiveStates(sidebar);
          sidebar.hide();
        }
      });
    }
  });

  // Close entire sidebar when clicking outside
  document.addEventListener('click', function (e) {
    // Don't close if clicking inside any sidebar or on hamburger buttons
    if (e.target.closest('header-sidebar') ||
      e.target.closest('[aria-controls^="sidebar-menu"]') ||
      e.target.closest('.header__icon--hamburger')) {
      return;
    }

    // Close all open sidebars completely
    allSidebars.forEach(sidebar => {
      if (sidebar.hasAttribute('open')) {
        resetAllActiveStates(sidebar);
        sidebar.hide();
      }
    });
  });
})();